export type FormanConfigOption = {
	value?: boolean | null | number | string | object | (boolean | null | number | string | object)[];
	label?: string;
	nested?: FormanConfigNested;
	default?: boolean;
	description?: string;
	short?: string;
};

type FormanConfigNestedStore = string | (string | FormanConfig)[];

type FormanConfigOptionsStore = string | FormanConfigOption[];

type FormanConfigNestedObject = {
	domain?: string;
	wrapper?: string;
	store: FormanConfigNestedStore;
};

type FormanConfigNested = string | (string | FormanConfig)[] | FormanConfigNestedObject;

type FormanConfigOptionObject = {
	store?: FormanConfigOptionsStore;
	nested?: FormanConfigNested;
	team?: number;
	value?: string;
	placeholder?: FormanConfigOption | string;
	/*
	 * Hook specific - if it's in the learning mode or not.
	 */
	learning?: boolean;
};

type FormanConfigOptions = string | FormanConfigOption[] | FormanConfigGroupOption[] | FormanConfigOptionObject;

interface FormanConfigGroupOption {
	label?: string;
	options: FormanConfigOption[];
}

export type FormanConfigSpec = (FormanConfig | string)[] | string | FormanConfig;

export interface FormanConfig {
	name?: string;
	label?: string;
	type?: string;
	value?: string;
	help?: string;
	default?: unknown;
	required?: boolean;
	mappable?: boolean | { help?: string; enabled?: boolean };
	validate?:
		| boolean
		| {
				max?: number;
				min?: number;
				pattern?: string;
				minItems?: number;
				maxItems?: number;
				const?: boolean | number | string;
		  };
	tags?: string[];
	grouped?: boolean;
	multiple?: boolean;
	time?: boolean;
	nested?: FormanConfigNested;
	options?: FormanConfigOptions;
	spec?: FormanConfigSpec;
	pattern?: string;
	forceReload?: boolean;
	advanced?: boolean;
	editable?: boolean;
	readonly?: boolean;
	labels?: unknown;
	mode?: string;
	theme?: string;
}

function normalizeArraySpec(arraySpec: FormanConfig[]): FormanConfig | null {
	if (arraySpec && Array.isArray(arraySpec) && arraySpec.length) {
		return {
			type: 'collection',
			spec: arraySpec,
		};
	}
	return null;
}

export function instructionToConfigSnapshot(
	instruction: FormanConfig | string,
	depth = 0,
	maxDepth = 20,
): FormanConfig | null {
	if (!instruction) {
		return null;
	}
	if (typeof instruction === 'string') {
		return null; // RPC strings are filtered out
	}

	if (depth >= maxDepth) {
		console.warn('Maximum recursion depth reached in configSnapshot conversion');
		return null;
	}

	const keys: (keyof FormanConfig)[] = [
		'name',
		'type',
		'label',
		'help',
		'required',
		'validate',
		'multiple',
		'tags',
		'time',
		'grouped',
	];
	const config: FormanConfig = {};
	for (const key of keys) {
		if (instruction[key]) {
			// @ts-expect-error unknown type
			config[key] = instruction[key];
		}
	}

	// Handle nested spec arrays recursively
	if (instruction.spec) {
		if (Array.isArray(instruction.spec)) {
			const configSpec = instruction.spec
				.map((spec) => instructionToConfigSnapshot(spec, depth + 1, maxDepth))
				.filter((s) => !!s);

			if (instruction.type === 'array') {
				const normalizedArraySpec = normalizeArraySpec(configSpec);
				if (normalizedArraySpec) {
					config.spec = normalizedArraySpec;
				}
			} else {
				config.spec = configSpec;
			}
		} else if (typeof instruction.spec === 'object') {
			const instructionSpec = instructionToConfigSnapshot(instruction.spec, depth + 1, maxDepth);
			if (instructionSpec) {
				if (instruction.type === 'array' && !('type' in instruction.spec)) {
					config.spec = { ...(instructionSpec as object), type: 'text' };
				} else {
					config.spec = instructionSpec;
				}
			}
		}
	}

	// Handle options recursively
	if (instruction.options) {
		if (Array.isArray(instruction.options)) {
			config.options = instruction.options.map((opt) => convertOption(opt, depth + 1, maxDepth)).filter((s) => !!s);
		} else if (typeof instruction.options === 'object') {
			const converted = convertOptionObject(instruction.options, depth + 1, maxDepth);
			if (converted) {
				config.options = converted;
			}
		}
	}

	return config;
}

export function convertOptionObject(
	option: FormanConfigOptionObject | string,
	depth = 0,
	maxDepth = 20,
): FormanConfigOptionObject | null {
	if (!option) {
		return null;
	}
	if (depth >= maxDepth) {
		console.warn('Maximum recursion depth reached in option conversion');
		return null;
	}
	if (typeof option === 'string') {
		return null; // RPC strings are filtered out
	}

	const convertedOptionObject: FormanConfigOptionObject = {
		...(option.team ? { team: option.team } : {}),
		...(option.value ? { value: option.value } : {}),
	};

	if ('placeholder' in option && option.placeholder) {
		const convertedPlaceholder = convertOption(option.placeholder, depth + 1, maxDepth);
		if (convertedPlaceholder) {
			convertedOptionObject.placeholder = convertedPlaceholder;
		}
	}

	if ('store' in option && Array.isArray(option.store)) {
		convertedOptionObject.store = option.store
			.map((nested) => convertOption(nested, depth + 1, maxDepth))
			.filter((s) => !!s);
	}

	return convertedOptionObject;
}

export function convertOption(
	option: string | FormanConfigOption | FormanConfigGroupOption,
	depth = 0,
	maxDepth = 20,
): FormanConfigOption | FormanConfigGroupOption | null {
	if (depth >= maxDepth) {
		console.warn('Maximum recursion depth reached in option conversion');
		return null;
	}
	if (typeof option === 'string') {
		return null; // RPC strings are filtered out
	}

	// Handle grouped options (option has its own options array)
	if ('options' in option && Array.isArray(option.options)) {
		return {
			...(option.label ? { label: option.label } : {}),
			options: option.options.map((opt) => convertOption(opt, depth + 1, maxDepth)).filter((s) => !!s),
		};
	}

	const keys: (keyof FormanConfigOption)[] = ['label', 'value', 'default', 'description', 'short'];
	const convertedOption: FormanConfigOption = {};
	for (const key of keys) {
		if ((option as FormanConfigOption)[key]) {
			// @ts-expect-error unknown type
			convertedOption[key] = option[key];
		}
	}

	return convertedOption;
}

// this helper is used only for array type
export function convertSpec(spec: FormanConfigSpec, maxDepth = 20): FormanConfig | null {
	if (Array.isArray(spec)) {
		const arraySpec = spec
			.map((instruction) => instructionToConfigSnapshot(instruction, 0, maxDepth))
			.filter((s) => !!s);
		return normalizeArraySpec(arraySpec);
	} else if (typeof spec === 'object' && spec !== null && Object.keys(spec).length !== 0) {
		if (!('type' in spec) && !('spec' in spec)) {
			return instructionToConfigSnapshot({ ...spec, type: 'text' }, 0, maxDepth) || null;
		}
		return instructionToConfigSnapshot(spec, 0, maxDepth) || null;
	}
	return null;
}
